import { useLocation, Link } from 'react-router-dom';
import { seoArticleContents } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.wiki';

export default function SeoArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').split('/')[0];
  const article = slug ? seoArticleContents[slug] : undefined;

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.date,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'OOPBuy Spreadsheet',
          url: SITE_URL,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/${slug}`,
        },
        inLanguage: 'de',
      }
    : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">Artikel nicht gefunden</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {articleJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
      )}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
              oopbuy<span className="text-primary-500">spread</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                  Startseite
                </span>
              </Link>
              <Link to="/oopbuy-spreadsheet-guide" className="hidden md:inline text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative w-full py-16 md:py-20 px-6 md:px-10 bg-background-100 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20open%20notebook%20and%20warm%20ambient%20lighting%20soft%20cream%20and%20beige%20tones%20clean%20aesthetic%20editorial%20photography%20subtle%20product%20accents%20gentle%20shadows%20harmonious%20composition&width=1920&height=600&seq=seo-article-hero&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-100/80 to-background-100"></div>
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">Startseite</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link to="/#seo-guides" className="hover:text-primary-600 transition-colors">SEO Guides</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-foreground-700">{article.title}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mb-4">{article.description}</p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.author}</span>
            </div>
          </div>
        </section>

        <article className="w-full py-12 md:py-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-sm md:prose-base max-w-none">
              {article.sections.map((section, i) => (
                <div key={i} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-14 pt-8 border-t border-background-200/70">
              <h4 className="font-heading text-lg font-bold text-foreground-900 mb-4">Interne Links</h4>
              <div className="flex flex-wrap gap-3">
                {article.internalLinks.map((link, i) => (
                  <Link
                    key={i}
                    to={link.url}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-background-100 border border-background-200/70 rounded-md text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 cursor-pointer"
                  >
                    {link.text}
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </Link>
                ))}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  oopbuy spreadsheet
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-external-link-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <footer className="w-full bg-background-900 text-background-200 py-10 px-6 md:px-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-heading text-lg font-bold text-background-50">
            oopbuy<span className="text-accent-400">spread</span>
          </Link>
          <p className="text-xs text-background-500">
            &copy; 2026 oopbuyspread.wiki – Unabhängige Informationsplattform
          </p>
        </div>
      </footer>
    </div>
  );
}